import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ApplicationPackage } from 'src/app/enums/application-package.enum';
import { Service } from 'src/app/enums/service.enum';
import { CustomHttpUrlEncodingCodec } from 'src/app/main/encoder';
import { ChannelsGenre } from 'src/app/model/cms-btv-core/channel-genre.model';
import { ChannelPackage } from 'src/app/model/cms-btv-core/channel-package.model';
import { ChannelQueryParams } from 'src/app/model/cms-btv-core/channel-query-params.model';
import { Channel } from 'src/app/model/cms-btv-core/channel.model';
import { ChannelFingerprint, ChannelFingerprintRequest } from 'src/app/model/cms-btv-core/channel-fingerprint.model';
import { BtvGenre, Genre } from 'src/app/model/cms-btv-core/genre.model';
import { PageOfChannel } from 'src/app/model/cms-btv-core/page-of-channel-model';
import { ProbeOutputP } from 'src/app/model/cms-btv-core/probe-outputP.model';
import { Program } from 'src/app/model/cms-btv-core/program-model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ChannelService {
  readonly channelApiUrl = environment.endPoint + ApplicationPackage.CMS_BTV + Service.CHANNEL;
  // readonly channelGenreApiUrl = 'http://localhost:8091/' + Service.GENRE;
  readonly channelGenreApiUrl = environment.endPoint + ApplicationPackage.CMS_BTV + Service.GENRE;
  readonly channelDetailApiUrl = environment.endPoint + ApplicationPackage.CMS_BTV + Service.CHANNEL_DETAIL;
  private channelUpdatedSource = new Subject<number>();
  channelUpdated$ = this.channelUpdatedSource.asObservable();

  constructor(private http: HttpClient) { }

  getAllChannel(channelQueryParams?: ChannelQueryParams): Observable<PageOfChannel> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (channelQueryParams.pageable) {
      queryParams = queryParams.set('pageable', <any>channelQueryParams.pageable);
    }

    if (channelQueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>channelQueryParams.itemCount);
    }

    channelQueryParams.page = channelQueryParams.page - 1;
    if (channelQueryParams.page >= 0) {
      queryParams = queryParams.set('page', <any>channelQueryParams.page);
    }
    if (channelQueryParams.status) {
      queryParams = queryParams.set('status', <any>channelQueryParams.status);
    }

    if (channelQueryParams.name) {
      queryParams = queryParams.set('name', <any>channelQueryParams.name);
    }

    if (channelQueryParams.mwChannelId) {
      queryParams = queryParams.set('mwChannelId', <any>channelQueryParams.mwChannelId);
    }

    if (channelQueryParams.mwPhysicalChannelCode) {
      queryParams = queryParams.set('mwPhysicalChannelCode', <any>channelQueryParams.mwPhysicalChannelCode);
    }

    if (channelQueryParams.pastEpg != undefined) {
      queryParams = queryParams.set('pastEpg', <any>channelQueryParams.pastEpg);
    }

    if (channelQueryParams.type) {
      queryParams = queryParams.set('type', <any>channelQueryParams.type);
    }

    if (channelQueryParams.genre) {
      queryParams = queryParams.set('genre', <any>channelQueryParams.genre);
    }

    if (channelQueryParams.categoryId != null && channelQueryParams.categoryId != undefined) {
      queryParams = queryParams.set('categoryId', <any>channelQueryParams.categoryId);
    }

    if (channelQueryParams.sortParam && channelQueryParams.sortParam.length > 0) {
      queryParams = queryParams.set('sortParam', <any>channelQueryParams.sortParam);
    }

    return this.http.get<PageOfChannel>(this.channelApiUrl, { params: queryParams });
  }

  getAllChannelWithOutPageable(channelQueryParams?: ChannelQueryParams): Observable<Channel[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (channelQueryParams.genre) {
      queryParams = queryParams.set('genre', <any>channelQueryParams.genre);
    }

    if (channelQueryParams.categoryId != null && channelQueryParams.categoryId != undefined) {
      queryParams = queryParams.set('categoryId', <any>channelQueryParams.categoryId);
    }

    if (channelQueryParams.status) {
      queryParams = queryParams.set('status', <any>channelQueryParams.status);
    }

    channelQueryParams.pageable = false;
    queryParams = queryParams.set('pageable', <any>channelQueryParams.pageable);

    return this.http.get<Channel[]>(this.channelApiUrl, { params: queryParams });
  }

  getChannelSummary(channelQueryParams?: ChannelQueryParams): Observable<{id: number, name: string}[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (channelQueryParams) {
      if (channelQueryParams.genre) {
        queryParams = queryParams.set('genre', <any>channelQueryParams.genre);
      }
      if (channelQueryParams.status) {
        queryParams = queryParams.set('status', <any>channelQueryParams.status);
      }
      if (channelQueryParams.name) {
        queryParams = queryParams.set('name', <any>channelQueryParams.name);
      }
      if (channelQueryParams.type) {
        queryParams = queryParams.set('type', <any>channelQueryParams.type);
      }
      if (channelQueryParams.platform) {
        queryParams = queryParams.set('platform', <any>channelQueryParams.platform);
      }
      if (channelQueryParams.categoryId != null && channelQueryParams.categoryId != undefined) {
        queryParams = queryParams.set('categoryId', <any>channelQueryParams.categoryId);
      }
      if (channelQueryParams.dvrEnabled != null && channelQueryParams.dvrEnabled != undefined) {
        queryParams = queryParams.set('dvrEnabled', <any>channelQueryParams.dvrEnabled);
      }
      if (channelQueryParams.excludeHd != null && channelQueryParams.excludeHd != undefined) {
        queryParams = queryParams.set('excludeHd', <any>channelQueryParams.excludeHd);
      }
    }
    return this.http.get<{id: number, name: string}[]>(this.channelApiUrl + '/summary', { params: queryParams });
  }

  getChannelById(channelId: number): Observable<Channel> {
    return this.http.get<Channel>(this.channelApiUrl + `/${channelId}`);
  }

  // Server-side count of non-DVB-C-only channels (used for the Atlas License dashboard).
  getLicensedChannelCount(): Observable<number> {
    return this.http.get<number>(this.channelApiUrl + '/license-count');
  }

  //* Get All Channel by status *//
  getAllChannelByOrder(status: boolean): Observable<PageOfChannel> {
    return this.http.get<PageOfChannel>(this.channelApiUrl + `?order=${status}`);
  }

  getChannelCdnPlayUrl(id: number, startTime: number, endTime: number): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (startTime) {
      queryParams = queryParams.set('startTime', <any>startTime);
    }

    if (endTime) {
      queryParams = queryParams.set('endTime', <any>endTime);
    }

    return this.http.get<string>(this.channelApiUrl + `/${id}/cdnplayrurl`, { params: queryParams, responseType: 'text' as 'json' });
  }
  getChannelCdnPlayUrlPromise(id: number, startTime: number, endTime: number): Promise<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (startTime) {
      queryParams = queryParams.set('startTime', <any>startTime);
    }

    if (endTime) {
      queryParams = queryParams.set('endTime', <any>endTime);
    }

    return this.http.get<string>(this.channelApiUrl + `/${id}/cdnplayrurl`, { params: queryParams, responseType: 'text' as 'json' }).toPromise();
  }
  //* Get Programs By QueryParameters **/
  getProgramsByChannelIdAndDate(channelId: number, startTime?: number, endTime?: number, releaseDate?: string): Observable<Program[]> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (startTime != null && startTime != undefined) {
      queryParams = queryParams.set('startTime', <any>startTime);
    }

    if (endTime != null && endTime != undefined) {
      queryParams = queryParams.set('endTime', <any>endTime);
    }

    if (releaseDate != null && releaseDate != undefined && releaseDate != '') {
      queryParams = queryParams.set('releaseDate', <any>releaseDate);
    }

    return this.http.get<Program[]>(`${this.channelApiUrl}/${channelId}/programs`, { params: queryParams });
  }
  /** Channel Search by genre */
  getAllGenre(): Observable<ChannelsGenre[]> {
    return this.http.get<ChannelsGenre[]>(this.channelGenreApiUrl);
  }

  /** Get Chhanels by genre */
  getChannelsByGenre(genre: Genre): Observable<PageOfChannel> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (genre !== null && genre !== undefined) {
      queryParams = queryParams.set('genre', <any>genre.name);
      queryParams = queryParams.set('pageable', <any>true);
    }
    return this.http.get<PageOfChannel>(this.channelApiUrl, { params: queryParams });
  }
  // Get Channel Programs
  getChannelPrograms(startTime: number, endTime: number, releaseDate: string): Observable<Program[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (startTime !== null && startTime != undefined) {
      queryParams = queryParams.set('startTime', <any>startTime);
    }
    if (endTime !== null && endTime !== undefined) {
      queryParams = queryParams.set('endTime', <any>endTime);
    }
    if (releaseDate !== null && releaseDate !== undefined && releaseDate !== '') {
      queryParams = queryParams.set('releaseDate', <any>releaseDate);
    }
    return this.http.get<Program[]>(this.channelApiUrl, { params: queryParams });
  }

  channelLogoUpload(channelId: number, file: File): Observable<string> {
    let formData = new FormData();
    formData.append('file', file);
    return this.http.post(this.channelApiUrl + `/${channelId}/logo`, formData, { responseType: 'text' });
  }
  channelLogoSecUpload(channelId: number, file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(this.channelApiUrl + `/${channelId}/logoSec`, formData, { responseType: 'text' });
  }
  probe(channelId: number, sourceUrl: string): Observable<ProbeOutputP[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (sourceUrl !== null && sourceUrl != undefined) {
      queryParams = queryParams.set('source', <any>sourceUrl);
    }
    return this.http.post<ProbeOutputP[]>(this.channelApiUrl + `/${channelId}/probe`, null, { params: queryParams });
  }
  createChannelInLive(channelId: number): Observable<string> {
    return this.http.post<string>(this.channelApiUrl + `/live/${channelId}/create`, null);
  }
  startRecord(channelId: number): Observable<any> {
    return this.http.get<any>(this.channelApiUrl + `/${channelId}/start`);
  }
  stopRecord(channelId: number): Observable<any> {
    return this.http.get<any>(this.channelApiUrl + `/${channelId}/stop`);
  }
  createChannelPackage(channelId: number, streamId: number[], channelPackage: ChannelPackage): Observable<number> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (streamId !== null && streamId != undefined) {
      queryParams = queryParams.set('streamId', <any>streamId);
    }
    return this.http.post<number>(this.channelApiUrl + `/${channelId}/channelpackage`, channelPackage, { params: queryParams });
  }

  // Create Channel
  createChannel(channel: Channel): Observable<Channel> {
    return this.http.post<Channel>(this.channelApiUrl, channel);
  }
  // Update/create Channel
  updateChannel(channelData: FormData): Observable<Channel> {
    return this.http.post<Channel>(this.channelApiUrl + '/', channelData);
  }

  notifyChannelUpdated(channelId: number): void {
    if (channelId != null) {
      this.channelUpdatedSource.next(channelId);
    }
  }

  //Delete Channel
  deleteChannel(channelId: number): Observable<number> {
    return this.http.delete<number>(this.channelApiUrl + `/${channelId}`);
  }

  updateChannelPriority(channels: Channel[]): Observable<Channel[]> {
    return this.http.put<Channel[]>(this.channelApiUrl + '/priority', channels, { responseType: 'text' as 'json' });
  }

  updatePastEPG(channelId: number, status: boolean): Observable<string> {
    return this.http.patch<string>(`${this.channelApiUrl}/${channelId}/updatePastPrograms`, {
      updatePastPrograms: status,
    });
  }

  updateAutoCheck(channelId: number, autoCheck: boolean): Observable<Channel> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    queryParams = queryParams.set('autoCheck', <any>autoCheck);
    return this.http.put<Channel>(this.channelApiUrl + `/${channelId}/autocheck`, null, { params: queryParams });
  }
  updateSmartSign(channelId: number, smartSign: boolean): Observable<Channel> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    queryParams = queryParams.set('smartSign', <any>smartSign);
    return this.http.put<Channel>(this.channelApiUrl + `/${channelId}/smartsign`, null, { params: queryParams });
  }

  getRestrictions(channelId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.channelDetailApiUrl}/${channelId}/restrictions`);
  }

  getQualities(channelId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.channelDetailApiUrl}/${channelId}/qualities`);
  }

  getPlannings(channelId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.channelDetailApiUrl}/${channelId}/plannings`);
  }

  getPlanningsSummary(channelId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.channelDetailApiUrl}/${channelId}/plannings/summary`);
  }

  saveRestrictions(channelId: number, restrictions: any[]): Observable<void> {
    return this.http.post<void>(`${this.channelDetailApiUrl}/${channelId}/restrictions`, restrictions);
  }

  saveQualities(channelId: number, qualities: any[]): Observable<void> {
    return this.http.post<void>(`${this.channelDetailApiUrl}/${channelId}/qualities`, qualities);
  }

  savePlannings(channelId: number, plannings: any[]): Observable<void> {
    return this.http.post<void>(`${this.channelDetailApiUrl}/${channelId}/plannings`, plannings);
  }

  updateChannelLanguageFields(channelId: number, audioContentLanguageNames: string,
    audioContentLanguageLabels: string, textContentLanguageNames: string,
    textContentLanguageLabels: string): Observable<any> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (audioContentLanguageNames != null) {
      queryParams = queryParams.set('audioContentLanguageNames', audioContentLanguageNames);
    }
    if (audioContentLanguageLabels != null) {
      queryParams = queryParams.set('audioContentLanguageLabels', audioContentLanguageLabels);
    }
    if (textContentLanguageNames != null) {
      queryParams = queryParams.set('textContentLanguageNames', textContentLanguageNames);
    }
    if (textContentLanguageLabels != null) {
      queryParams = queryParams.set('textContentLanguageLabels', textContentLanguageLabels);
    }
    return this.http.put(this.channelApiUrl + `/${channelId}/channellanguagefields`, null, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  hasPlannings(channelId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.channelDetailApiUrl}/${channelId}/has-platform-plan`);
  }

  getChannelFingerprints(channelId: number): Observable<ChannelFingerprint[]> {
    return this.http.get<ChannelFingerprint[]>(`${this.channelDetailApiUrl}/${channelId}/fingerprints`);
  }

  createChannelFingerprint(channelId: number, request: ChannelFingerprintRequest): Observable<ChannelFingerprint> {
    return this.http.post<ChannelFingerprint>(`${this.channelDetailApiUrl}/${channelId}/fingerprints`, request);
  }

  updateChannelFingerprint(
    channelId: number,
    fingerprintId: number,
    request: ChannelFingerprintRequest
  ): Observable<ChannelFingerprint> {
    return this.http.put<ChannelFingerprint>(
      `${this.channelDetailApiUrl}/${channelId}/fingerprints/${fingerprintId}`,
      request
    );
  }

  deleteChannelFingerprint(channelId: number, fingerprintId: number): Observable<void> {
    return this.http.delete<void>(`${this.channelDetailApiUrl}/${channelId}/fingerprints/${fingerprintId}`);
  }
}
